<script setup lang="ts">
// Structured add/remove rows for a task version's gates, with a JSON toggle for power users.
// Every field is the kit `Input` and each stored row sits on a kit surface (#1227): no border, fill or radius here.

import { Button, Checkbox, Input, ZhPanel } from '@zerohuman/ui'
import { ref } from 'vue'
import RowsJsonSection from '@/components/RowsJsonSection.vue'
import { useRowsModel } from '@/composables/useRowsModel'

/**
 * One gate. `match` narrows it to the calls whose payload fields start with (a list) or equal (a scalar) what it
 * names; `requiresSummary` holds the call until the run has written a gate summary; `oneAskPer` names the payload
 * fields that say what a call acts on, so a later ask on the same target replaces an earlier one still waiting;
 * `editable` names the payload fields the approver may change before approving (#433), set in the JSON view and
 * kept as it is by the rows. Each is optional and left out of the value when unset, so a gate saved before they
 * existed round-trips unchanged.
 */
export type TaskGate = {
    tool: string
    owner: string
    approval: string
    match?: Record<string, unknown>
    requiresSummary?: boolean
    oneAskPer?: string[]
    editable?: string[]
}

/**
 * A row carries `match.argv` as one text field, the command's words separated by spaces, and `oneAskPer` as one text
 * field, the payload field names separated by commas.
 */
type GateRow = TaskGate & { argv: string; requiresSummary: boolean; oneAskPerText: string }

const props = defineProps<{ modelValue: TaskGate[] }>()
const emit = defineEmits<{ 'update:modelValue': [TaskGate[]] }>()

function argvOf(gate: TaskGate): string {
    const argv = gate.match?.argv
    return Array.isArray(argv) ? argv.map(String).join(' ') : ''
}

function oneAskPerTextOf(gate: TaskGate): string {
    return Array.isArray(gate.oneAskPer) ? gate.oneAskPer.join(', ') : ''
}

/** The field names a comma-separated `oneAskPer` text holds, blanks dropped. */
function oneAskPerOf(text: string): string[] {
    return text
        .split(',')
        .map(field => field.trim())
        .filter(Boolean)
}

/**
 * The stored shape: `match`, `oneAskPer` and `editable` only when they name something, `requiresSummary` only when
 * true. `editable` has no field of its own on a row, so it is carried as the row was given it: editing a row's other
 * fields never drops what its approver may edit.
 */
function gateOf(row: GateRow): TaskGate {
    const match: Record<string, unknown> = { ...(row.match ?? {}) }
    const argv = row.argv.trim()
    if (argv) {
        match.argv = argv.split(/\s+/)
    } else {
        delete match.argv
    }
    const oneAskPer = oneAskPerOf(row.oneAskPerText)
    return {
        tool: row.tool,
        owner: row.owner,
        approval: row.approval,
        ...(Object.keys(match).length > 0 ? { match } : {}),
        ...(row.requiresSummary ? { requiresSummary: true } : {}),
        ...(oneAskPer.length > 0 ? { oneAskPer } : {}),
        ...(Array.isArray(row.editable) && row.editable.length > 0 ? { editable: row.editable } : {}),
    }
}

const { rows, showJson, jsonText, jsonError, append, removeAt, toggleJson } = useRowsModel<GateRow, TaskGate>({
    modelValue: () => props.modelValue,
    emit: value => emit('update:modelValue', value),
    toRow: gate => ({
        ...gate,
        argv: argvOf(gate),
        requiresSummary: gate.requiresSummary === true,
        oneAskPerText: oneAskPerTextOf(gate),
    }),
    toValue: gateOf,
})

const draft = ref<GateRow>(emptyGate())

function emptyGate(): GateRow {
    return { tool: '', owner: 'ceo', approval: 'manual', argv: '', requiresSummary: false, oneAskPerText: '' }
}

/** A gate without a tool holds nothing back, so it is not worth a row. */
function add(): void {
    const tool = draft.value.tool.trim()
    if (!tool) {
        return
    }
    append({ ...draft.value, tool })
    draft.value = emptyGate()
}
</script>

<template>
	<RowsJsonSection
		testid="task-gates"
		title="Gates"
		:show-json="showJson"
		:json-text="jsonText"
		:json-error="jsonError"
		:json-rows="4"
		@update:json-text="jsonText = $event"
		@toggle="toggleJson"
	>
		<template #description>A tool call that waits for someone before it runs.</template>
		<p v-if="!rows.length" class="text-muted-foreground text-sm">No gates: every tool call runs unattended.</p>
		<ul class="space-y-2">
			<ZhPanel
				v-for="(row, index) in rows"
				:key="index"
				as="li"
				data-testid="task-gate-row"
				class="flex flex-wrap items-end gap-2"
			>
				<label class="grid gap-1 text-xs">
					Tool
					<Input v-model="row.tool" />
				</label>
				<label class="grid gap-1 text-xs">
					Owner
					<Input v-model="row.owner" />
				</label>
				<label class="grid gap-1 text-xs">
					Approval
					<Input v-model="row.approval" />
				</label>
				<label class="grid gap-1 text-xs">
					Only when argv starts with
					<Input v-model="row.argv" data-testid="task-gate-argv" placeholder="mise run os:infra:apply" />
				</label>
				<label class="grid gap-1 text-xs">
					One waiting ask per
					<Input v-model="row.oneAskPerText" data-testid="task-gate-one-ask-per" placeholder="owner, repo, index" />
				</label>
				<label class="flex items-center gap-1 text-xs">
					<Checkbox v-model="row.requiresSummary" data-testid="task-gate-requires-summary" />
					Needs a summary
				</label>
				<Button type="button" size="sm" variant="destructive" data-testid="task-gate-remove" @click="removeAt(index)">
					Remove
				</Button>
			</ZhPanel>
		</ul>
		<div class="flex flex-wrap items-end gap-2">
			<label class="grid gap-1 text-xs">
				Tool
				<Input v-model="draft.tool" data-testid="task-gate-add-tool" placeholder="vendor.tool_name" />
			</label>
			<label class="grid gap-1 text-xs">
				Owner
				<Input v-model="draft.owner" data-testid="task-gate-add-owner" placeholder="ceo" />
			</label>
			<label class="grid gap-1 text-xs">
				Approval
				<Input v-model="draft.approval" data-testid="task-gate-add-approval" placeholder="manual" />
			</label>
			<label class="grid gap-1 text-xs">
				Only when argv starts with
				<Input v-model="draft.argv" data-testid="task-gate-add-argv" placeholder="mise run os:infra:apply" />
			</label>
			<label class="grid gap-1 text-xs">
				One waiting ask per
				<Input v-model="draft.oneAskPerText" data-testid="task-gate-add-one-ask-per" placeholder="owner, repo, index" />
			</label>
			<label class="flex items-center gap-1 text-xs">
				<Checkbox v-model="draft.requiresSummary" data-testid="task-gate-add-requires-summary" />
				Needs a summary
			</label>
			<Button type="button" size="sm" variant="outline" data-testid="task-gate-add" @click="add">Add gate</Button>
		</div>
	</RowsJsonSection>
</template>
